import { forwardRef, type FC, type ReactNode, type HTMLAttributes } from 'react';
import type { MessageDescriptor } from 'react-intl';
import styled, { useTheme, type DefaultTheme } from 'styled-components';
import { UNIT_TRANSLATED, type Unit } from '@enmon/commons/lib/shared';
import { isNil } from 'ramda';
import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import FormattedNumber from '@/components/FormattedNumber';
import { Label } from '@/components/typography/components/Label';
import { TypographySize } from '@/components/typography/enums/size';
import { TypographyType } from '@/components/typography/enums/type';
import { Tooltip } from '@/components/tooltip/Tooltip';
import { NumberCellWrapper } from './NumberCell';

/**
 * When only one of the two compared values is known, the whole known value counts as the
 * difference (and the percentage difference is 100%).
 */
export const getAbsoluteDifference = (a: number | undefined, b: number | undefined): number | undefined => {
  if (isNil(a) && isNil(b)) return undefined;
  if (isNil(a)) return Math.abs(b as number);
  if (isNil(b)) return Math.abs(a);
  return Math.abs(a - b);
};

export const getPercentageDifference = (value: number | undefined, base: number | undefined): number | undefined => {
  if (isNil(value) && isNil(base)) return undefined;
  if (isNil(value) || isNil(base)) return 100;
  // NOTE: a zero base means there's nothing to compare against - the whole value counts as the
  // difference (100%), same as when the base is missing entirely. Equal zeros mean no difference.
  if (base === 0) return value === 0 ? 0 : 100;
  return (Math.abs(value - base) / base) * 100;
};

export const getDifferenceColor = (percentageDifference: number, theme: DefaultTheme): string => {
  if (percentageDifference <= 2) return theme.color.core.green.main[700];
  if (percentageDifference <= 10) return theme.color.core.orange.main[900];
  return theme.color.core.red.main[700];
};

/** Small, gray unit label rendered after a numeric value in a cell (not part of the header). */
export const UnitSuffix: FC<{ unit?: Unit }> = ({ unit }) => {
  if (!unit) return null;
  return (
    <>
      {' '}
      <Label size={TypographySize.Small} type={TypographyType.Secondary}>
        {UNIT_TRANSLATED[unit]}
      </Label>
    </>
  );
};

/** Matches the app/table typography instead of antd's tooltip font token. */
const DifferenceTooltipContent = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.poppins};
`;

/**
 * Explains how a difference was calculated: the formula with the row's actual numbers plus its
 * translated textual form, e.g. "(313 - 323)" / "(Measured Consumption - Invoiced Consumption)".
 */
export const DifferenceTooltip: FC<{
  variant: 'absolute' | 'percentage';
  baseValue: number;
  comparedValue: number;
  messages: { absolute: MessageDescriptor; percentage: MessageDescriptor };
  children: ReactNode;
}> = ({ variant, baseValue, comparedValue, messages, children }) => (
  <Tooltip
    title={
      <DifferenceTooltipContent>
        (<FormattedNumber value={baseValue} /> - <FormattedNumber value={comparedValue} />)
        {variant === 'percentage' && (
          <>
            {' / '}
            <FormattedNumber value={baseValue} /> * 100
          </>
        )}
        <br />
        <FormattedMessage {...messages[variant]} />
      </DifferenceTooltipContent>
    }
  >
    {children}
  </Tooltip>
);

/**
 * Only the number is colored by the deviation thresholds; the % sign stays small and gray.
 * Ref + rest props land on the wrapper DOM node so an enclosing antd Tooltip can attach and position itself.
 */
export const DifferenceValue = forwardRef<HTMLDivElement, { value: number } & HTMLAttributes<HTMLElement>>(
  ({ value, ...rest }, ref) => {
    const theme = useTheme();
    return (
      <NumberCellWrapper ref={ref} {...rest}>
        <span style={{ color: getDifferenceColor(value, theme) }}>
          <FormattedNumber minFraction={2} value={value} />
        </span>{' '}
        <Label size={TypographySize.Small} type={TypographyType.Secondary}>
          %
        </Label>
      </NumberCellWrapper>
    );
  },
);
